import { Box, Button, Chip, CircularProgress, Dialog, IconButton, TextField, Typography } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";
import { useEffect, useState } from "react";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

type SelectablePayoutStatus = "Processing" | "Released" | "Failed";

const PAYOUT_OPTIONS: { value: SelectablePayoutStatus; label: string }[] = [
  { value: "Processing", label: "Processing" },
  { value: "Released", label: "Released" },
  { value: "Failed", label: "Failed" },
];

export default function UpdatePayoutDialog({
  open,
  onClose,
  onConfirm,
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: { payoutStatus: SelectablePayoutStatus; payoutReference?: string; payoutRemarks?: string }) => void;
  loading?: boolean;
}) {
  const [payoutStatus, setPayoutStatus] = useState<SelectablePayoutStatus | null>(null);
  const [payoutReference, setPayoutReference] = useState("");
  const [payoutRemarks, setPayoutRemarks] = useState("");

  useEffect(() => {
    if (open) {
      setPayoutStatus(null);
      setPayoutReference("");
      setPayoutRemarks("");
    }
  }, [open]);

  const requiresReference = payoutStatus === "Released";
  const requiresRemarks = payoutStatus === "Failed";
  const canSubmit =
    !!payoutStatus &&
    (!requiresReference || !!payoutReference.trim()) &&
    (!requiresRemarks || !!payoutRemarks.trim());

  const handleConfirm = () => {
    if (!payoutStatus) return;
    onConfirm({
      payoutStatus,
      ...(requiresReference ? { payoutReference: payoutReference.trim() } : {}),
      ...(requiresRemarks ? { payoutRemarks: payoutRemarks.trim() } : {}),
    });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ width: 420, maxWidth: "90vw", p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: purpleLight,
                flexShrink: 0,
              }}
            >
              <PaymentsOutlined sx={{ color: purple, fontSize: 20 }} />
            </Box>
            <Typography fontWeight={700} fontSize={17} color={black}>
              Update Payout Status
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={loading}>
            <CloseRounded fontSize="small" sx={{ color: "#5B4A5A" }} />
          </IconButton>
        </Box>

        <Typography variant="body2" fontWeight={600} color={black} mb={1}>
          Payout Status
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          {PAYOUT_OPTIONS.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              clickable
              variant={payoutStatus === option.value ? "filled" : "outlined"}
              color="primary"
              onClick={() => setPayoutStatus(option.value)}
            />
          ))}
        </Box>

        {requiresReference && (
          <TextField
            fullWidth
            label="Payout Reference"
            placeholder="Enter the payment/transaction reference"
            value={payoutReference}
            onChange={(e) => setPayoutReference(e.target.value.slice(0, 100))}
            sx={{ mb: 2 }}
          />
        )}
        {requiresRemarks && (
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Remarks"
            placeholder="Why did the payout fail? (points will be refunded)"
            value={payoutRemarks}
            onChange={(e) => setPayoutRemarks(e.target.value.slice(0, 500))}
            helperText={`${payoutRemarks.length}/500`}
            sx={{ mb: 2 }}
          />
        )}
        {payoutStatus === "Processing" && (
          <Typography variant="body2" color={greyEnabled} mb={2}>
            Marks the payout as being worked on - no points are affected.
          </Typography>
        )}

        <Box mt={1} display="flex" gap={1.5}>
          <Button
            fullWidth
            variant="outlined"
            disabled={loading}
            onClick={onClose}
            sx={{
              borderColor: purple,
              color: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { borderColor: purple, bgcolor: purpleLight },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            disabled={loading || !canSubmit}
            onClick={handleConfirm}
            sx={{
              bgcolor: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#7A2074" },
            }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Update"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
